import React, { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import MobileShell from "@/components/mobile/MobileShell";
import { api } from "@/lib/api";
import { fmtDateIT, resolveStatoHex } from "@/lib/format";
import { Search, X, ChevronRight, Phone, Loader2, Filter, MessageSquareText } from "lucide-react";
import QuickMsgSheet from "@/components/QuickMsgSheet";
import SwipeActions from "@/components/mobile/SwipeActions";
import { toast } from "sonner";
import { telLink, buzz } from "@/lib/mobile";

const TABS = [
  { id: "all",      label: "Tutti" },
  { id: "daily",    label: "Oggi" },
  { id: "expired",  label: "Scaduti" },
  { id: "hot",      label: "Caldi" },
];

export default function MobileClients() {
  const [params, setParams] = useSearchParams();
  const view    = params.get("view") || "all";
  const [q, setQ]           = useState("");
  const [items, setItems]   = useState([]);
  const [total, setTotal]   = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage]     = useState(0);
  const [msgClient, setMsgClient] = useState(null);
  const PAGE = 40;

  const load = useCallback(async (reset = true) => {
    setLoading(true);
    try {
      const r = await api.get("/clients", {
        params: { view: view === "all" ? undefined : view, q: q || undefined, limit: PAGE, offset: reset ? 0 : page * PAGE, sort: "updated_desc" }
      });
      if (reset) {
        setItems(r.data.items || []);
        setPage(0);
      } else {
        setItems(prev => [...prev, ...(r.data.items || [])]);
      }
      setTotal(r.data.total || 0);
    } catch {} finally { setLoading(false); }
  }, [view, q, page]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(true); }, [view, q]);

  // Hint swipe (una sola volta)
  useEffect(() => {
    if (items.length === 0) return;
    try {
      if (!localStorage.getItem("ba-swipe-hint-v1")) {
        localStorage.setItem("ba-swipe-hint-v1", "1");
        toast("💡 Scorri una card: → destra per chiamare, ← sinistra per il messaggio", { duration: 5500 });
      }
    } catch {}
  }, [items.length]);

  const setView = (v) => setParams(v === "all" ? {} : { view: v });

  return (
    <MobileShell title="Clienti" subtitle={`${total.toLocaleString("it-IT")} nel portafoglio`}>
      {/* Search */}
      <div className="relative mb-3">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" style={{ color: "#94A3B8" }} strokeWidth={2} />
        <input
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder="Cerca nome, telefono, città…"
          data-testid="mobile-clients-search"
          className="w-full pl-10 pr-9 py-3 text-[13px] transition-all"
          style={{ background: "#fff", border: "1px solid #E8ECF2", borderRadius: 12, outline: "none" }}
          onFocus={e => e.target.style.borderColor = "#0A192F"}
          onBlur={e => e.target.style.borderColor = "#E8ECF2"}
        />
        {q && (
          <button onClick={() => setQ("")} className="absolute right-3.5 top-1/2 -translate-y-1/2">
            <X className="w-4 h-4" style={{ color: "#94A3B8" }} />
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1.5 overflow-x-auto no-scrollbar mb-4 pb-0.5">
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setView(t.id)}
            data-testid={`mtab-${t.id}`}
            className="text-[12px] font-semibold px-3.5 py-2 whitespace-nowrap transition-all active:scale-[0.97] shrink-0"
            style={{
              background: view === t.id ? "#0A192F" : "#fff",
              color: view === t.id ? "#fff" : "#64748B",
              border: `1px solid ${view === t.id ? "#0A192F" : "#E8ECF2"}`,
              borderRadius: 20,
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* List */}
      {loading && items.length === 0 ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-5 h-5 animate-spin" style={{ color: "#B7955B" }} />
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center py-12 text-center">
          <p className="text-[14px] font-medium text-slate-500">Nessun cliente trovato</p>
          <p className="text-[12px] text-slate-400 mt-1">Prova a cambiare i filtri</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map(c => <ClientCard key={c.id} c={c} onMsg={setMsgClient} />)}
          {items.length < total && (
            <button
              onClick={() => { setPage(p => p + 1); load(false); }}
              className="w-full py-3.5 text-[13px] font-semibold transition-colors"
              style={{ background: "#fff", border: "1px solid #E8ECF2", borderRadius: 12, color: "#B7955B" }}
            >
              Carica altri ({total - items.length} rimanenti)
            </button>
          )}
        </div>
      )}

      {/* Sheet messaggi preimpostati */}
      <QuickMsgSheet client={msgClient} open={!!msgClient} onOpenChange={(v) => { if (!v) setMsgClient(null); }} />
    </MobileShell>
  );
}

function ClientCard({ c, onMsg }) {
  const statoBg = c.stato ? resolveStatoHex(c.stato) : "#E2E8F0";
  const initials = (c.cognome || c.nome || "?").slice(0, 1).toUpperCase();
  const recall = c.data ? fmtDateIT(c.data) : null;
  const today = new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Rome" });
  const isExpired = c.data && c.data < today;
  const isToday   = c.data && c.data === today;

  return (
    <SwipeActions
      disabled={!c.telefono}
      radius={12}
      onSwipeRight={() => { window.location.href = telLink(c.telefono); }}
      onSwipeLeft={() => onMsg?.(c)}
      testid={`mclient-swipe-${c.id}`}
    >
    <Link
      to={`/m/clienti/${c.id}`}
      className="flex items-center gap-3 px-4 py-3.5 active:scale-[0.99] transition-transform"
      style={{
        background: "#fff",
        border: "1px solid #E8ECF2",
        borderLeft: `3px solid ${statoBg}`,
        borderRadius: 12,
        boxShadow: "0 1px 3px rgba(10,25,47,0.04)",
      }}
      data-testid={`mclient-${c.id}`}
    >
      {/* Avatar */}
      <div
        className="w-9 h-9 rounded-full flex items-center justify-center text-[13px] font-semibold shrink-0"
        style={{ background: `${statoBg}20`, color: statoBg }}
      >
        {initials}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-semibold text-slate-800 truncate">{c.cognome} {c.nome}</p>
        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
          {c.stato && (
            <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 text-white" style={{ background: statoBg, borderRadius: 3, fontSize: 9 }}>
              {c.stato}
            </span>
          )}
          {recall && (
            <span className={`text-[11px] tabular-nums font-medium ${isExpired ? "text-rose-500" : isToday ? "text-amber-600" : "text-slate-400"}`}>
              {isExpired ? "⚠️ " : isToday ? "📞 " : ""}{recall}
            </span>
          )}
          {c.citta && <span className="text-[11px] text-slate-400 truncate">{c.citta}</span>}
        </div>
      </div>

      {/* Azioni rapide: chiama + msg */}
      {c.telefono ? (
        <div className="flex items-center gap-1.5 shrink-0">
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); buzz(); window.location.href = telLink(c.telefono); }}
            data-testid={`mclient-call-${c.id}`}
            className="w-8 h-8 rounded-full flex items-center justify-center active:scale-90 transition-transform"
            style={{ background: "rgba(22,163,74,0.10)", color: "#16A34A" }}
            aria-label="Chiama"
          >
            <Phone className="w-3.5 h-3.5" strokeWidth={2.2} />
          </button>
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); buzz(); onMsg?.(c); }}
            data-testid={`mclient-msg-${c.id}`}
            className="w-8 h-8 rounded-full flex items-center justify-center active:scale-90 transition-transform"
            style={{ background: "rgba(2,132,199,0.10)", color: "#0284C7" }}
            aria-label="Messaggio preimpostato"
          >
            <MessageSquareText className="w-3.5 h-3.5" strokeWidth={2.2} />
          </button>
        </div>
      ) : (
        <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" strokeWidth={1.5} />
      )}
    </Link>
    </SwipeActions>
  );
}
